import { styled } from "@linaria/react";
import * as io from "io-ts";
import React from "react";
import { DEFAULT_PAGINATION_VALUE, PaginationQueryType } from "../../../../api/pagination";
import t from "../../../../i18n";
import createGlobalState from "@ui/lib/createGlobalState";
import useDebounce from "@ui/lib/hooks/useDebounce";
import useModal from "@ui/lib/hooks/useModal";
import Button from "@ui/Button";
import InlineMenu from "@ui/InlineMenu";
import Modal from "@ui/Modal";
import Table from "@ui/Table";
import TableCard from "@ui/Table/Card";
import { PageSizeSelector, PageSwitcher } from "@ui/Table/Pagination";
import Filters, { useFilterValues } from "./Filters";
import ModalForm from "./ModalForm";
import type { Column } from "./useColumns";
import { COLUMNS, requiredVisibleColumns, useColumns } from "./useColumns";
import useMetadata from "./useMetadata";
import Option from "@ui/Option";
import Link from "@ui/Link";
import { AddSelection, CancelSelection } from "@ui/Table/useSelectedRows";
import { useCanUserDo } from "../../../canUserDo";
import type { ClassificationQuantity } from "../../../../api/useClassificationQuantities";
import { useClassificationQuantities } from "../../../../api/useClassificationQuantities";

const StyledInlineMenu = styled(InlineMenu)`
  font-size: 14px;
`;

const useGlobalQuantitiesSearch = createGlobalState({
  storage: "session",
  key: "administrationQuantitiesSearch",
  type: io.string,
  defaultValue: "",
  scoped: false,
});

const useSelectedQuantities = createGlobalState({
  storage: "session",
  key: "selectedAdministrationQuantities",
  type: io.array(io.string),
  defaultValue: [],
  scoped: false,
});

const useDisplayDraft = createGlobalState({
  storage: "session",
  key: "administrationQuantitiesTableDisplayDraft",
  type: io.boolean,
  defaultValue: false,
  scoped: false,
});

const usePagination = createGlobalState({
  storage: "session",
  key: "adminQuantitiesTablePagination",
  type: PaginationQueryType,
  defaultValue: DEFAULT_PAGINATION_VALUE,
  scoped: false,
});

const computeRowKey = (q: ClassificationQuantity) => `quantity-id-${q.id}`;

export const QuantityClassification = () => {
  const userHasCategoryEdit = useCanUserDo("CATEGORY_EDIT");
  const userHasAllProjectCategoryEdit = useCanUserDo("PROJECT_CATEGORY_EDIT");
  const [visibleColumns, setVisibleColumns] = useColumns({});
  const tableMetadata = useMetadata();
  const [menuOpened, setMenuOpened] = React.useState(false);
  const [displayDraft, setDisplayDraft] = useDisplayDraft({});
  const [pagination, setPagination] = usePagination({});
  const { modal, openModal, closeModal } = useModal();
  const searchState = useGlobalQuantitiesSearch({});
  const [selectedQuantities, setSelectedQuantities] = useSelectedQuantities({});
  const [selectAction, setSelectAction] = React.useState(selectedQuantities.length > 0);

  const deferredSelectAction = React.useDeferredValue(selectAction);

  const handleDisplayDraftChange = React.useCallback(
    () => setDisplayDraft(!displayDraft),
    [setDisplayDraft, displayDraft],
  );

  const cancelSelection = React.useCallback(() => {
    setSelectedQuantities([]);
    setSelectAction(false);
  }, [setSelectedQuantities]);

  const resetPagination = React.useCallback(
    () => setPagination(currentPagination => ({ ...currentPagination, page: 1 })),
    [setPagination],
  );

  const [filterValues, setFilterValues] = useFilterValues({});

  const apiFilters = React.useMemo(() => {
    const projectIds = filterValues.project;
    const projectTypeIds = filterValues.projectType;
    const projectSiteIds = filterValues.projectSite;
    const systemIds = filterValues.system;
    const name = searchState[0];

    return {
      name: name.length === 0 ? null : encodeURI(searchState[0]),
      systemIds,
      project: {
        ids: projectIds,
        siteIds: projectSiteIds,
        typeIds: projectTypeIds,
        includeDraft: displayDraft,
      },
    };
  }, [
    filterValues.project,
    filterValues.projectType,
    filterValues.projectSite,
    filterValues.system,
    searchState,
    displayDraft,
  ]);

  const apiParams = React.useMemo(
    () => ({
      query: pagination,
      body: apiFilters,
    }),
    [apiFilters, pagination],
  );

  const deferredApiParams = React.useDeferredValue(useDebounce(apiParams, 300));
  const dataOutdated = deferredApiParams !== apiParams;
  const { elements: quantities, totalElements } = useClassificationQuantities(deferredApiParams);

  const pageChangedHandler = React.useCallback(
    (pageIndex: number) => {
      setPagination(state => ({ page: Math.max(pageIndex + 1, 1), pageSize: state.pageSize }));
    },
    [setPagination],
  );

  const pageSizeChangeHandler = React.useCallback(
    (size: number) => setPagination({ page: 1, pageSize: size }),
    [setPagination],
  );

  const onRowsSelection = React.useCallback(
    (data: ClassificationQuantity[]) => {
      setSelectedQuantities(data.map(({ id }) => id));
    },
    [setSelectedQuantities],
  );

  const selectedDatas = React.useMemo(
    () => quantities.filter(({ id }) => selectedQuantities.includes(id)),
    [quantities, selectedQuantities],
  );

  return (
    <>
      <Modal
        title={t("administration.quantities.table.header.actions.update", {
          count: selectedQuantities.length,
        })}
        isOpen={modal}
        onRequestClose={closeModal}
      >
        <ModalForm onRequestClose={closeModal} selectedQuantities={selectedDatas} />
      </Modal>
      <TableCard
        title={null}
        headerLeft={
          <Filters
            displayDraft={displayDraft}
            onDisplayDraftChange={handleDisplayDraftChange}
            searchState={searchState}
            resetPagination={resetPagination}
            filterValues={filterValues}
            setFilterValues={setFilterValues}
          />
        }
        headerRight={
          <>
            {selectAction ? (
              <>
                <Button
                  slim
                  variant="primary"
                  onClick={openModal}
                  disabled={!selectedQuantities.length}
                  title={t("administration.table.quantities.selection_button_title")}
                >
                  {t("administration.quantities.table.header.actions.update", {
                    count: selectedQuantities.length,
                  })}
                </Button>
                <CancelSelection onClick={cancelSelection}>
                  {t("projects.header.actions.cancel_selection")}
                </CancelSelection>
              </>
            ) : (
              <>
                <StyledInlineMenu
                  anchor="right"
                  opened={menuOpened}
                  setOpened={setMenuOpened}
                  caret
                  overlay={COLUMNS.map(column => (
                    <Option
                      key={column}
                      checkbox
                      disabled={requiredVisibleColumns.includes(column)}
                      selected={visibleColumns.includes(column)}
                      onClick={() =>
                        setVisibleColumns(
                          visibleColumns.includes(column)
                            ? visibleColumns.filter(c => c !== column)
                            : [...visibleColumns, column],
                        )
                      }
                    >
                      {tableMetadata[column].label}
                    </Option>
                  ))}
                >
                  <Link>{t("commons.select.manage_columns")}</Link>
                </StyledInlineMenu>
                {(userHasCategoryEdit || userHasAllProjectCategoryEdit) && (
                  <AddSelection
                    slim
                    onClick={() => setSelectAction(true)}
                    title={t("administration.table.quantities.selection_button_title")}
                  >
                    {t("administration.quantities.table.header.actions.edit")}
                  </AddSelection>
                )}
              </>
            )}
          </>
        }
        pagination={
          <>
            <PageSizeSelector
              currentSelectionValue={pagination.pageSize}
              onPageSizeChanged={pageSizeChangeHandler}
            />
            <PageSwitcher
              pageIndex={pagination.page - 1}
              pageSize={pagination.pageSize}
              elementNumber={totalElements}
              onPageChanged={pageChangedHandler}
            />
          </>
        }
      >
        <Table<ClassificationQuantity, Column>
          stickyHeader
          onRowsSelection={deferredSelectAction ? onRowsSelection : undefined}
          datas={quantities}
          selectedDatas={selectedDatas}
          metadata={tableMetadata}
          computeRowKey={computeRowKey}
          loading={dataOutdated}
        />
      </TableCard>
    </>
  );
};
